import { useEffect, useState } from "react";
import { App, Alert, Button, Flex, Modal, Skeleton } from "antd";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import {
  useAttachPaymentMethodMutation,
  useCreateSetupIntentMutation,
} from "api";
import { getStripePromise } from "libs/stripe";
import { getErrorMessage } from "components";

interface AddPaymentMethodModalProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

/**
 * Outer component: handles fetching the SetupIntent client_secret, then mounts
 * <Elements> with that secret so the inner form can render <PaymentElement>.
 *
 * Splitting the inner form into a separate component is required by Stripe —
 * useStripe() and useElements() only work inside an <Elements> provider.
 */
export const AddPaymentMethodModal = ({
  open,
  onClose,
  onSaved,
}: AddPaymentMethodModalProps) => {
  const { notification } = App.useApp();
  const [createSetupIntent, { isLoading: isCreatingIntent }] =
    useCreateSetupIntentMutation();

  const [clientSecret, setClientSecret] = useState<string | null>(null);

  // Reset between opens so a new SetupIntent is fetched each time.
  useEffect(() => {
    if (!open) {
      setClientSecret(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const result = await createSetupIntent(undefined).unwrap();
        if (!cancelled) {
          setClientSecret(result.data.client_secret);
        }
      } catch (e) {
        if (!cancelled) {
          notification.error({ message: getErrorMessage(e as never) });
          onClose();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Modal
      title="Adauga metoda de plata"
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={520}
    >
      {(isCreatingIntent || !clientSecret) && (
        <Skeleton active paragraph={{ rows: 4 }} />
      )}
      {clientSecret && (
        <Elements
          stripe={getStripePromise()}
          options={{
            clientSecret,
            locale: "ro",
            appearance: { theme: "stripe" },
          }}
        >
          <AddPaymentMethodForm onClose={onClose} onSaved={onSaved} />
        </Elements>
      )}
    </Modal>
  );
};

interface AddPaymentMethodFormProps {
  onClose: () => void;
  onSaved?: () => void;
}

const AddPaymentMethodForm = ({
  onClose,
  onSaved,
}: AddPaymentMethodFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const { notification } = App.useApp();
  const [attachPaymentMethod, { isLoading: isAttaching }] =
    useAttachPaymentMethodMutation();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) {
      // Stripe.js still loading — guard against early click.
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setErrorMessage(submitError.message ?? "Eroare validare card.");
      setSubmitting(false);
      return;
    }

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: "if_required",
      confirmParams: {
        // Required by Stripe for redirect-based methods (e.g. SCA). For card-only
        // SetupIntents this is rarely used but still required by the SDK.
        return_url: window.location.href,
      },
    });

    if (error) {
      setErrorMessage(error.message ?? "Stripe a refuzat cardul.");
      setSubmitting(false);
      return;
    }

    if (!setupIntent || !setupIntent.payment_method) {
      setErrorMessage("Stripe nu a returnat un payment_method. Reincearca.");
      setSubmitting(false);
      return;
    }

    try {
      await attachPaymentMethod({
        payment_method_id: setupIntent.payment_method as string,
      }).unwrap();
      notification.success({ message: "Cardul a fost salvat." });
      onSaved?.();
      onClose();
    } catch (e) {
      notification.error({ message: getErrorMessage(e as never) });
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit}>
      <PaymentElement />
      {errorMessage && (
        <Alert
          type="error"
          showIcon
          message={errorMessage}
          style={{ marginTop: 16 }}
        />
      )}
      <Flex justify="flex-end" gap={8} style={{ marginTop: 24 }}>
        <Button onClick={onClose} disabled={submitting || isAttaching}>
          Anuleaza
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={submitting || isAttaching}
          disabled={!stripe}
        >
          Salveaza cardul
        </Button>
      </Flex>
    </form>
  );
};
